import React from 'react';
import { Check, RotateCcw } from 'lucide-react';
import { DropdownMenuItem } from './ui/dropdown-menu.tsx';
import { cn } from '../lib/utils';
import { useTheme } from '../contexts/ThemeContext';
import {
  SIDEBAR_COLOUR_PRESETS,
  DEFAULT_SIDEBAR_SWATCHES,
  normaliseSidebarHex,
  isDarkColour,
} from '../utils/sidebarColour';

const stop = (e) => e.stopPropagation();
const keepOpen = (e) => e.preventDefault();

const SWATCH_BASE =
  'h-6 w-6 p-0 rounded-full justify-center border border-black/10 cursor-pointer focus:ring-2 focus:ring-ring focus:ring-inset';
const SELECTED = 'ring-2 ring-inset ring-foreground/60';
const IDLE = 'hover:scale-110 transition-transform';
const LINK = 'inline-flex items-center gap-1 px-1 py-0.5 text-xs cursor-pointer whitespace-nowrap';
const LINK_ON = 'text-muted-foreground hover:text-foreground';
const LINK_OFF = 'text-muted-foreground/50';

function Tick({ hex }) {
  return <Check className={cn('h-3 w-3', isDarkColour(hex) ? 'text-white' : 'text-black')} />;
}

/**
 * "Sidebar colour" section of the Theme menu. The sidebar and the surround
 * around the main panel take this one colour (text/highlight/border are
 * derived); Light/Dark and the colour theme keep painting the main panel only.
 *
 * Exactly one option is highlighted at all times: the "Theme default" swatch
 * (the colour the current theme paints by itself), one of the ten swatches,
 * or the Custom control for a colour that is in no swatch.
 *
 * The ten swatches are the user's own palette: with a swatch selected, the
 * Custom colour wheel replaces that swatch's colour (and keeps it selected);
 * with Theme default or a free custom colour active, the wheel just picks a
 * colour. "Reset" returns to the theme's own colour and the built-in ten
 * swatches. Everything is stored per browser, so the palette and the
 * highlight survive reloads.
 */
export default function SidebarColourPicker() {
  const {
    sidebarColour,
    setSidebarColour,
    themeSidebarDefault,
    sidebarSwatches,
    setSidebarSwatch,
    resetSidebarSwatches,
  } = useTheme();
  const swatches = sidebarSwatches || DEFAULT_SIDEBAR_SWATCHES;
  const current = normaliseSidebarHex(sidebarColour);
  const selectedIndex = current ? swatches.indexOf(current) : -1;
  const isDefault = !current;
  const isCustom = !!current && selectedIndex < 0;
  const defaultHex = normaliseSidebarHex(themeSidebarDefault);
  const customValue = current || defaultHex || '#1e2a44';
  const swatchesEdited = swatches.some((hex, i) => hex !== DEFAULT_SIDEBAR_SWATCHES[i]);
  const canReset = !isDefault || swatchesEdited;
  const reset = () => {
    setSidebarColour(null);
    resetSidebarSwatches();
  };

  const handleCustom = (e) => {
    const hex = normaliseSidebarHex(e.target.value);
    if (!hex) return;
    if (selectedIndex >= 0) setSidebarSwatch(selectedIndex, hex);
    setSidebarColour(hex);
  };

  return (
    <div className="px-2 py-1.5" data-testid="sidebar-colour-picker">
      <DropdownMenuItem
        onSelect={keepOpen}
        onClick={() => setSidebarColour(null)}
        className={cn(
          'flex items-center gap-2 px-1 py-1 text-xs cursor-pointer rounded-md',
          isDefault ? 'text-foreground font-medium' : 'text-muted-foreground hover:text-foreground',
        )}
        aria-pressed={isDefault}
        data-testid="sidebar-colour-default"
        title="Use the colour the current theme gives the sidebar"
      >
        <span
          className={cn(
            'h-6 w-6 rounded-full inline-flex items-center justify-center border border-black/10 shrink-0',
            isDefault ? SELECTED : '',
          )}
          style={{ background: defaultHex || 'hsl(var(--sidebar-background))' }}
          aria-hidden="true"
        >
          {isDefault ? <Tick hex={defaultHex || '#ffffff'} /> : null}
        </span>
        Theme default
      </DropdownMenuItem>
      <div className="mt-1.5 grid grid-cols-5 gap-1.5" role="group" aria-label="Sidebar colour swatches">
        {swatches.map((hex, i) => {
          const selected = i === selectedIndex;
          const name = hex === DEFAULT_SIDEBAR_SWATCHES[i] ? SIDEBAR_COLOUR_PRESETS[i].name : `Swatch ${i + 1}`;
          return (
            <DropdownMenuItem
              key={i}
              onSelect={keepOpen}
              onClick={() => setSidebarColour(hex)}
              className={cn(SWATCH_BASE, selected ? SELECTED : IDLE)}
              style={{ background: hex }}
              title={`${name} (${hex}) — select, then use Custom to replace it`}
              aria-label={`${name} sidebar colour`}
              aria-pressed={selected}
              data-testid={`sidebar-swatch-${i}`}
            >
              {selected ? <Tick hex={hex} /> : null}
            </DropdownMenuItem>
          );
        })}
      </div>
      <div className="mt-2 flex items-center justify-between gap-3">
        <label
          className={cn(
            'inline-flex items-center gap-1.5 text-xs cursor-pointer whitespace-nowrap',
            isCustom ? 'text-foreground font-medium' : 'text-muted-foreground',
          )}
          onKeyDown={stop}
          data-testid="sidebar-colour-custom"
          data-selected={isCustom ? 'true' : 'false'}
          title={selectedIndex >= 0 ? `Replaces swatch ${selectedIndex + 1}` : 'Pick any colour'}
        >
          <input
            type="color"
            value={customValue}
            onChange={handleCustom}
            onKeyDown={stop}
            className={cn(
              'h-6 w-8 p-0 border rounded cursor-pointer bg-transparent',
              isCustom ? 'border-foreground/60 ring-2 ring-inset ring-foreground/60' : 'border-border',
            )}
            aria-label={selectedIndex >= 0 ? `Custom colour, replaces swatch ${selectedIndex + 1}` : 'Custom sidebar colour'}
          />
          {selectedIndex >= 0 ? `Edit swatch ${selectedIndex + 1}` : 'Custom'}
        </label>
        <DropdownMenuItem
          onSelect={keepOpen}
          onClick={reset}
          disabled={!canReset}
          className={cn(LINK, canReset ? LINK_ON : LINK_OFF)}
          title="Back to the theme's sidebar colour and the built-in swatches"
          data-testid="sidebar-colour-reset"
        >
          <RotateCcw className="h-3 w-3" /> Reset
        </DropdownMenuItem>
      </div>
    </div>
  );
}
